import { useEffect, useRef, useState } from "react";
import {
  CaretLeft, VideoCamera, Phone, Checks, Microphone, Plus, Camera,
} from "@phosphor-icons/react";

/**
 * PhoneShowcase — premium iPhone mockup running a LIVE WhatsApp
 * conversation loop. The customer's perspective: green bubbles are the
 * client, white bubbles are the AgentUS-powered business replying with
 * real operational context (vehicle, history, schedule) — the "this is
 * not a chatbot" moment, staged instead of claimed.
 *
 * Pure CSS/React — no video. Respects prefers-reduced-motion (renders
 * the first scenario statically).
 */

const SCENARIOS = [
  [
    { from: "client", text: "Oi! O carro tá com um barulho estranho na roda dianteira 😕 Consegue me encaixar amanhã?", time: "09:41" },
    { from: "ai", text: "Claro, Ricardo! Vi aqui que seu Corolla 2022 também tá com a revisão dos 30 mil chegando. Aproveito e resolvo os dois?", time: "09:41" },
    { from: "client", text: "Boa!! Pode ser 👏", time: "09:42" },
    { from: "ai", text: "Fechado ✅ Amanhã às 9h40 — barulho na roda + revisão. Te mando um lembrete hoje à noite!", time: "09:42" },
  ],
  [
    { from: "ai", text: "Oi, Marcos! Faz 6 meses da troca de óleo do seu HB20 🚗 Quer que eu já reserve um horário essa semana?", time: "14:07" },
    { from: "client", audio: "0:07", time: "14:09" },
    { from: "ai", text: "Entendi seu áudio 😄 Quinta depois das 15h, certo? Reservei 15h30 pra você ✅", time: "14:09" },
    { from: "client", text: "Respondeu até meu áudio kkk confirmado 🙌", time: "14:10" },
  ],
  [
    { from: "ai", text: "Ana, seu Compass ficou pronto ✅ Alinhamento + balanceamento concluídos. Pode retirar até as 18h.", time: "16:32" },
    { from: "client", text: "Maravilha! Chego às 17h", time: "16:33" },
    { from: "ai", text: "Combinado! E pode deixar que eu te aviso quando chegar a época do próximo alinhamento 😉", time: "16:33" },
  ],
];

const WAVE = [4, 8, 5, 10, 6, 9, 4, 11, 7, 5, 9, 6, 10, 4, 7, 5, 8, 4, 6, 3];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const fn = (e) => setReduced(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return reduced;
}

function useConversation(reduced, scenarios) {
  const [messages, setMessages] = useState(reduced ? scenarios[0] : []);
  const [typing, setTyping] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (reduced) { setMessages(scenarios[0]); return undefined; }
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((res) => { timers.push(setTimeout(res, ms)); });

    (async () => {
      let s = 0;
      while (!cancelled) {
        setFading(false);
        setMessages([]);
        await wait(1000);
        const scenario = scenarios[s % scenarios.length];
        for (const msg of scenario) {
          if (cancelled) return;
          if (msg.from === "ai") {
            await wait(700);
            setTyping(true);
            await wait(1700);
            setTyping(false);
          } else {
            await wait(1200);
          }
          if (cancelled) return;
          setMessages((m) => [...m, msg]);
          await wait(1900);
        }
        await wait(3400);
        if (cancelled) return;
        setFading(true);
        await wait(650);
        s += 1;
      }
    })();

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [reduced, scenarios]);

  return { messages, typing, fading };
}

function AudioBubble({ duration }) {
  return (
    <div className="flex items-center gap-2 py-1 pr-1">
      <span className="w-6 h-6 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
        <Microphone size={12} weight="fill" className="text-white" />
      </span>
      <svg width="86" height="18" viewBox="0 0 86 18" aria-hidden>
        {WAVE.map((h, i) => (
          <rect key={i} x={i * 4.4} y={(18 - h) / 2} width="2.2" height={h} rx="1.1" fill="#7B9E7E" />
        ))}
      </svg>
      <span className="text-[9px] text-[#667781] shrink-0">{duration}</span>
    </div>
  );
}

function Bubble({ msg }) {
  const mine = msg.from === "client";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} wa-msg-in`}>
      <div
        className={`relative max-w-[82%] px-2.5 pt-1.5 pb-[15px] text-[12px] leading-[1.38] text-[#111B21] shadow-[0_1px_1px_rgba(11,20,26,0.13)] ${
          mine
            ? "bg-[#D9FDD3] rounded-[10px] rounded-tr-[2px]"
            : "bg-white rounded-[10px] rounded-tl-[2px]"
        }`}
      >
        {msg.audio ? <AudioBubble duration={msg.audio} /> : msg.text}
        <span className="absolute bottom-[3px] right-[7px] flex items-center gap-0.5 text-[8.5px] text-[#667781]">
          {msg.time}
          {mine && <Checks size={11} weight="bold" className="text-[#53BDEB]" />}
        </span>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start wa-msg-in">
      <div className="bg-white rounded-[10px] rounded-tl-[2px] px-3 py-2.5 shadow-[0_1px_1px_rgba(11,20,26,0.13)]">
        <div className="flex items-center gap-[3px]">
          {[0, 1, 2].map((i) => (
            <span key={i} className="wa-typing-dot" style={{ animationDelay: `${i * 0.18}s` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PhoneShowcase({
  style,
  scenarios = SCENARIOS,
  contactName = "Auto Center Premium",
  contactInitials,
}) {
  const reduced = usePrefersReducedMotion();
  const { messages, typing, fading } = useConversation(reduced, scenarios);
  const scrollRef = useRef(null);

  // Auto-derive initials from name if not provided (first letter of first
  // 2 words). Falls back to "AP" so the chip is never empty.
  const initials = (
    contactInitials ||
    contactName
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() ||
    "AP"
  ).slice(0, 2);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: reduced ? "auto" : "smooth" });
  }, [messages, typing, reduced]);

  return (
    <div
      data-testid="login-phone-showcase"
      className={reduced ? "" : "wa-phone-float"}
      style={{ height: "clamp(440px, 58vh, 620px)", aspectRatio: "9 / 19", ...style }}
    >
      {/* Titanium frame */}
      <div
        className="relative w-full h-full rounded-[13.5%_/_6.5%] p-[3px]"
        style={{
          background: "linear-gradient(155deg, #4A4E55 0%, #23262B 30%, #15171B 65%, #3A3E45 100%)",
          boxShadow:
            "0 60px 110px -30px rgba(0,0,0,0.85), 0 22px 45px -18px rgba(0,0,0,0.7), inset 0 0 2px rgba(255,255,255,0.28)",
        }}
      >
        <div className="relative w-full h-full rounded-[13%_/_6.2%] bg-black p-[7px]">
          {/* Screen */}
          <div className="relative w-full h-full rounded-[11.5%_/_5.5%] overflow-hidden flex flex-col bg-[#075E54]">
            {/* Dynamic Island */}
            <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[30%] h-[3.6%] bg-black rounded-full z-20" />

            {/* Status bar */}
            <div className="flex items-end justify-between px-5 pt-2.5 pb-1 text-white shrink-0">
              <span className="text-[10px] font-semibold tracking-tight">9:41</span>
              <span className="flex items-center gap-1" aria-hidden>
                <svg width="14" height="9" viewBox="0 0 14 9"><g fill="#fff"><rect x="0" y="6" width="2.4" height="3" rx="0.6"/><rect x="3.6" y="4" width="2.4" height="5" rx="0.6"/><rect x="7.2" y="2" width="2.4" height="7" rx="0.6"/><rect x="10.8" y="0" width="2.4" height="9" rx="0.6" opacity="0.4"/></g></svg>
                <svg width="19" height="9" viewBox="0 0 19 9"><rect x="0.5" y="0.5" width="15" height="8" rx="2.2" fill="none" stroke="#fff" strokeOpacity="0.5"/><rect x="2" y="2" width="10" height="5" rx="1" fill="#fff"/><rect x="16.5" y="2.8" width="1.6" height="3.4" rx="0.8" fill="#fff" fillOpacity="0.5"/></svg>
              </span>
            </div>

            {/* WhatsApp header */}
            <div className="flex items-center gap-2 px-2.5 pb-2 pt-0.5 text-white shrink-0">
              <CaretLeft size={15} weight="bold" className="opacity-90" />
              <span className="w-[26px] h-[26px] rounded-full bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-[9px] font-bold shrink-0 select-none">
                {initials}
              </span>
              <div className="flex-1 min-w-0 leading-none">
                <p className="text-[11.5px] font-semibold tracking-tight truncate">{contactName}</p>
                <p className="text-[8.5px] opacity-75 mt-[2px]">
                  {typing ? "digitando…" : "online"}
                </p>
              </div>
              <VideoCamera size={15} weight="fill" className="opacity-90 mx-1" />
              <Phone size={13} weight="fill" className="opacity-90 mx-1" />
            </div>

            {/* Chat area */}
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-hidden px-2.5 py-2.5 space-y-[7px] transition-opacity duration-500"
              style={{
                opacity: fading ? 0 : 1,
                backgroundColor: "#ECE5DD",
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(69,90,86,0.13) 1px, transparent 0)",
                backgroundSize: "14px 14px",
              }}
            >
              <div className="flex justify-center">
                <span className="text-[8.5px] uppercase tracking-wide bg-[#DDD3C4]/90 text-[#5A6A72] px-2 py-[3px] rounded-md shadow-sm select-none">
                  Hoje
                </span>
              </div>
              {messages.map((msg, i) => <Bubble key={i} msg={msg} />)}
              {typing && <TypingBubble />}
            </div>

            {/* Input bar */}
            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#F0F2F5] shrink-0">
              <Plus size={16} className="text-[#54656F] shrink-0 mx-0.5" />
              <div className="flex-1 h-[26px] bg-white rounded-full px-3 flex items-center text-[10.5px] text-[#8696A0] select-none">
                Mensagem
              </div>
              <Camera size={15} className="text-[#54656F] shrink-0" />
              <span className="w-[26px] h-[26px] rounded-full bg-[#00A884] flex items-center justify-center shrink-0">
                <Microphone size={12} weight="fill" className="text-white" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .wa-msg-in { animation: waMsgIn .32s cubic-bezier(0.21, 1.02, 0.55, 1) both; }
        @keyframes waMsgIn {
          from { opacity: 0; transform: translateY(9px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .wa-typing-dot {
          width: 5px; height: 5px; border-radius: 9999px; background: #93A5AD;
          display: inline-block; animation: waTyping 1.1s ease-in-out infinite;
        }
        @keyframes waTyping {
          0%, 60%, 100% { transform: translateY(0); opacity: .45; }
          30% { transform: translateY(-3px); opacity: 1; }
        }
        .wa-phone-float { animation: waPhoneFloat 7s ease-in-out infinite; }
        @keyframes waPhoneFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-9px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .wa-msg-in, .wa-typing-dot, .wa-phone-float { animation: none; }
        }
      `}</style>
    </div>
  );
}
